import { inject, type InjectionKey, type Ref } from 'vue'

import type { OnboardingFormStepKey } from '../lib/onboardingFormSteps'
import type {
  MissingOnboardingArea,
  OnboardingFormStepStatus,
} from './useOnboardingDataCompleteness'

/**
 * Required-field state for the enter-data form, provided by the wizard
 * (index.vue) from `useOnboardingDataCompleteness` + `useOnboardingFormSteps`
 * so the form, the continue guards and the final submit all use the same rules.
 */
export type OnboardingValidationContext = {
  /** Missing required fields of the current form step only. */
  missingFieldsByArea: Ref<MissingOnboardingArea[]>
  /** Error for a field regardless of whether the user has visited it yet. */
  fieldError: (key: string) => string
  /** Whether the field must be filled to finish onboarding (shows the asterisk). */
  isFieldRequired: (key: string) => boolean
  /** True once the user tried to leave the current step with missing data: show every error. */
  missingFieldsRevealed: Ref<boolean>
  formSteps: {
    currentIndex: Ref<number>
    currentStep: Ref<OnboardingFormStepKey>
    isLast: Ref<boolean>
    status: Ref<Record<OnboardingFormStepKey, OnboardingFormStepStatus>>
    requiredProgress: Ref<{ filled: number; total: number }>
    goTo: (index: number) => boolean
    next: () => boolean
    previous: () => void
    show: (step: OnboardingFormStepKey) => void
  }
}

export const onboardingValidationKey: InjectionKey<OnboardingValidationContext> =
  Symbol('onboardingValidation')

export function useOnboardingValidationContext(): OnboardingValidationContext | null {
  return inject(onboardingValidationKey, null)
}
